import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { OperatingArea } from '@freelancer/datastore/collections';
import { UserAvailabilityFilters } from '@freelancer/search-filters';
import { trackById } from '@freelancer/ui';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, Observable } from 'rxjs';
import type { ChecklistOption } from '../../search-checklist-filter/search-checklist-filter.types';
import type {
  UserAvailabilityPreferredDropOffFormType,
  UserAvailabilityPreferredPickupFormType,
  UserAvailabilitySearchFiltersFormType,
} from '../search-user-availabilities.model';

@UntilDestroy({ className: 'SearchUserAvailabilitiesFiltersComponent' })
@Component({
  standalone: false,
  selector: 'app-search-user-availabilities-filters',
  template: `
    <fl-grid class="Filters">
      <fl-col
        [col]="12"
        [colTablet]="8"
      >
        <div class="Filters-locationDropdownContainer">
          <div class="Filters-locationDropdown">
            <fl-label
              i18n="Preferred pickup filter label"
              [color]="labelFontColor"
              [for]="'PreferredPickupArea'"
              [sizeTablet]="TextSize.BODY_16"
              [size]="TextSize.BODY_16"
              [weight]="FontWeight.BOLD"
            >
              Preferred pickup
            </fl-label>
            <app-freightlancer-search-filters-loads-location
              class="tablet:mb-none mb-mid"
              flTrackingSection="PreferredPickupAreaDropdownFilter"
              [buttonTextApplied]="preferredPickupAreasAppliedText$ | flAsync"
              [dropdownFilterId]="'PreferredPickupArea'"
              [locationFormGroup]="preferredPickupAreasFormGroup"
              [operatingAreas$]="operatingAreas$"
            >
            </app-freightlancer-search-filters-loads-location>
          </div>

          <fl-icon
            class="Filters-switchLocationsIcon mobile:hide"
            flTrackingLabel="SwitchLocationsIcon"
            [clickable]="true"
            [color]="IconColor.PRIMARY"
            [name]="'ui-switch'"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            [fillIconFont]="true"
            [weight]="FontWeight.MEDIUM"
            (click)="handleSwitchLocationsButtonClick()"
          ></fl-icon>

          <div class="Filters-locationDropdown">
            <fl-label
              i18n="Preferred dropoff filter label"
              [color]="labelFontColor"
              [sizeTablet]="TextSize.BODY_16"
              [size]="TextSize.BODY_16"
              [weight]="FontWeight.BOLD"
              [for]="'PreferredDropOffArea'"
            >
              Preferred dropoff
            </fl-label>
            <app-freightlancer-search-filters-loads-location
              class="tablet:mb-none mb-mid"
              flTrackingSection="PreferredDropOffAreaDropdownFilter"
              [buttonTextApplied]="preferredDropOffAreasAppliedText$ | flAsync"
              [dropdownFilterId]="'PreferredDropOffArea'"
              [locationFormGroup]="preferredDropOffAreasFormGroup"
              [operatingAreas$]="operatingAreas$"
            >
            </app-freightlancer-search-filters-loads-location>
          </div>
        </div>
      </fl-col>
      <fl-col
        [col]="12"
        [colTablet]="4"
      >
        <div class="Filters-vehicleTypeDropdownContainer">
          <fl-label
            i18n="Vehicle type filter label"
            [color]="labelFontColor"
            [for]="'VehicleType'"
            [sizeTablet]="TextSize.BODY_16"
            [size]="TextSize.BODY_16"
            [weight]="FontWeight.BOLD"
          >
            Vehicle type
          </fl-label>
          <fl-dropdown-filter
            buttonText="All vehicle types"
            class="Filters-vehicleTypeDropdown"
            i18n-buttonText="Vehicle type dropdown default button text"
            [buttonTextApplied]="vehicleTypeButtonTextApplied$ | flAsync"
            [filterApplied]="
              (vehicleTypeButtonTextApplied$ | flAsync) !== undefined
            "
            [id]="'VehicleType'"
          >
            @if (vehicleTypeChecklistOptions$ | flAsync; as checklistOptions) {
              <div class="Filters-vehicleTypeDropdown-checkboxContainer">
                @for (
                  option of checklistOptions;
                  track trackById($index, option)
                ) {
                  <fl-checkbox
                    class="Filters-vehicleTypeDropdown-checkbox mb-small"
                    flTrackingLabel="VehicleTypeDropdownCheckbox"
                    [control]="option.control"
                    [label]="option.displayValue"
                  ></fl-checkbox>
                }
              </div>
            }
          </fl-dropdown-filter>
        </div>
      </fl-col>
    </fl-grid>
  `,
  styleUrls: ['./search-user-availabilities-filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchUserAvailabilitiesFiltersComponent {
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  TextSize = TextSize;
  trackById = trackById;

  @Input() labelFontColor: FontColor = FontColor.LIGHT;
  @Input() filters: FormGroup<UserAvailabilitySearchFiltersFormType>;
  @Input() operatingAreas$: Observable<readonly OperatingArea[]>;
  @Input() operatingAreaChecklistOptions$: Observable<
    readonly ChecklistOption[]
  >;
  @Input() preferredDropOffAreasAppliedText$: Observable<string | undefined>;
  @Input()
  preferredDropOffAreasFormGroup: FormGroup<UserAvailabilityPreferredDropOffFormType>;
  @Input() preferredPickupAreasAppliedText$: Observable<string | undefined>;
  @Input()
  preferredPickupAreasFormGroup: FormGroup<UserAvailabilityPreferredPickupFormType>;
  @Input() vehicleTypeButtonTextApplied$: Observable<string | undefined>;
  @Input() vehicleTypeChecklistOptions$: Observable<readonly ChecklistOption[]>;

  handleSwitchLocationsButtonClick(): void {
    const tempPickupFormValues =
      this.filters.controls[UserAvailabilityFilters.PREFERRED_PICKUP_AREAS]
        .value;
    const tempDropOffFormValues =
      this.filters.controls[UserAvailabilityFilters.PREFERRED_DROPOFF_AREAS]
        .value;

    firstValueFrom(
      this.operatingAreaChecklistOptions$.pipe(untilDestroyed(this)),
    ).then(options => {
      options.forEach(option => {
        this.preferredPickupAreasFormGroup
          .get(option.id)
          ?.setValue(
            tempDropOffFormValues.find(
              (value: number) => value.toString() === option.id,
            ),
          );
        this.preferredDropOffAreasFormGroup
          .get(option.id)
          ?.setValue(
            tempPickupFormValues.find(
              (value: number) => value.toString() === option.id,
            ),
          );
      });
    });
  }
}
